import type { CloudWord, DriverRow, Sentiment } from "../Types/ListeningReportTypes";

const DRIVER_SORT_KEYS = ["neg", "pos", "net"] as const;
export type DriverSortKey = (typeof DRIVER_SORT_KEYS)[number];
export const isDriverSortKey = (value: unknown): value is DriverSortKey =>
  DRIVER_SORT_KEYS.some(key => key === value);
export type DriverStatus = "strength" | "watch" | "risk";

const MAX_WORD_EXAMPLES = 3;

export const pluralize = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

const editDistance = (first: string, second: string): number => {
  let previous = Array.from({ length: second.length + 1 }, (_, index) => index);
  for (let row = 1; row <= first.length; row += 1) {
    const current = [row];
    for (let column = 1; column <= second.length; column += 1) {
      const substitution = previous[column - 1] + (first[row - 1] === second[column - 1] ? 0 : 1);
      current.push(Math.min(substitution, previous[column] + 1, current[column - 1] + 1));
    }
    previous = current;
  }
  return previous[second.length];
};

const MIN_BRAND_WORD_LENGTH = 5;

/** The agent sometimes "corrects" a brand's spelling in its prose; show the name the user typed instead. */
export const alignBrandNames = (text: string, brandNames: string[]): string =>
  text.replace(/[\p{L}\p{N}]+/gu, word =>
    brandNames.find(name => {
      const target = name.toLowerCase();
      const candidate = word.toLowerCase();
      return (
        target.length >= MIN_BRAND_WORD_LENGTH &&
        !/\s/.test(name) &&
        candidate.charAt(0) === target.charAt(0) &&
        editDistance(candidate, target) <= 1
      );
    }) ?? word
  );

export const getDriverNet = (driver: DriverRow) => driver.positive - driver.negative;

export const getDriverStatus = (driver: DriverRow): DriverStatus | null => {
  if (driver.positive + driver.neutral + driver.negative === 0) {
    return null;
  }
  const net = getDriverNet(driver);
  if (net > 0) {
    return "strength";
  }
  return net < 0 ? "risk" : "watch";
};

export const sortDrivers = (drivers: DriverRow[], key: DriverSortKey): DriverRow[] =>
  [...drivers].sort((a, b) => {
    if (key === "pos") {
      return b.positive - a.positive;
    }
    if (key === "net") {
      return getDriverNet(a) - getDriverNet(b);
    }
    return b.negative - a.negative || a.positive - b.positive;
  });

export const formatSignedNumber = (value: number, decimals = 0) => {
  const text = Math.abs(value).toFixed(decimals);
  if (value > 0) {
    return `+${text}`;
  }
  return value < 0 ? `−${text}` : text;
};

export const getSignedTone = (value: number): "pos" | "neg" | undefined => {
  if (value > 0) {
    return "pos";
  }
  return value < 0 ? "neg" : undefined;
};

/** Alternates words around the centre so the biggest ones sit in the middle of the cloud. */
export const orderCloudWords = (words: CloudWord[]): CloudWord[] =>
  [...words]
    .sort((a, b) => b.weight - a.weight)
    .reduce<CloudWord[]>((ordered, word, index) => {
      if (index % 2) {
        ordered.push(word);
      } else {
        ordered.unshift(word);
      }
      return ordered;
    }, []);

export const getTopWords = (words: CloudWord[], sentiment: Sentiment, count = 5): CloudWord[] =>
  words.filter(word => word.sentiment === sentiment).slice(0, count);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getWordPattern = (word: string): RegExp => {
  const source = word === "color" ? "colou?r" : escapeRegExp(word);
  return new RegExp(`\\b(${source})\\b`, "i");
};

export const findWordExamples = (word: string, comments: string[]): string[] => {
  const pattern = getWordPattern(word);
  return comments.filter(comment => pattern.test(comment)).slice(0, MAX_WORD_EXAMPLES);
};

/** Splits a comment so the odd entries are the occurrences of the word. */
export const splitByWord = (text: string, word: string): string[] =>
  text.split(getWordPattern(word));
